import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChangeAdoptionKind, ChangeAdoptionKindValues } from '@paladin-monorepo/common';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { projectPortfolioApi, changeAdoptionQueryKey } from '@/lib/api/project-portfolio';
import { projectsApi } from '@/lib/api/projects';

const NONE = '__none';

type FormValues = {
  kind: ChangeAdoptionKind;
  title: string;
  detail: string;
  ownerUserId: string;
  target: string;
  dueDate: string;
};

const schema = yup.object({
  kind: yup.mixed<ChangeAdoptionKind>().oneOf(ChangeAdoptionKindValues).required(),
  title: yup.string().trim().required(),
  detail: yup.string().default(''),
  ownerUserId: yup.string().default(NONE),
  target: yup.string().default(''),
  dueDate: yup.string().default(''),
});

export function ProjectChangeAdoptionCard({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: changeAdoptionQueryKey(projectId),
    queryFn: () => projectPortfolioApi.listAdoption(projectId),
    retry: false,
  });
  const { data: participants = [] } = useQuery({
    queryKey: ['project-comment-participants', projectId],
    queryFn: () => projectsApi.listCommentParticipants(projectId),
    retry: false,
  });
  const nameOf = (userId: string | null) =>
    userId ? (participants.find((p) => p.id === userId)?.displayName ?? userId) : t('workspace.adoption.none');
  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      kind: ChangeAdoptionKind.AffectedGroup,
      title: '',
      detail: '',
      ownerUserId: NONE,
      target: '',
      dueDate: '',
    },
  });
  const create = useMutation({
    mutationFn: (values: FormValues) =>
      projectPortfolioApi.createAdoption(projectId, {
        kind: values.kind,
        title: values.title,
        detail: values.detail || null,
        ownerUserId: values.ownerUserId === NONE ? null : values.ownerUserId,
        target: values.target || null,
        dueDate: values.dueDate || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changeAdoptionQueryKey(projectId) });
      form.reset();
    },
  });
  const toggle = useMutation({
    mutationFn: (item: { id: string; version: number; done: boolean }) =>
      projectPortfolioApi.updateAdoption(projectId, item.id, {
        expectedVersion: item.version,
        done: !item.done,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: changeAdoptionQueryKey(projectId) }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.adoption.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('workspace.adoption.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-2 text-sm">
                <div>
                  <Badge variant="secondary">{t(`workspace.adoption.kind.${item.kind}`)}</Badge>
                  <span className={item.done ? 'ml-2 line-through text-muted-foreground' : 'ml-2'}>{item.title}</span>
                  {item.detail ? <p className="text-muted-foreground">{item.detail}</p> : null}
                  <p className="text-xs text-muted-foreground">
                    {t('workspace.adoption.owner')}: {nameOf(item.ownerUserId)}
                    {item.target ? ` · ${t('workspace.adoption.target')}: ${item.target}` : ''}
                    {item.dueDate ? ` · ${t('workspace.adoption.dueDate')}: ${item.dueDate}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => toggle.mutate(item)}>
                  {t('workspace.adoption.done')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form className="space-y-2" onSubmit={form.handleSubmit((v) => create.mutate(v))}>
          <Label>{t('workspace.adoption.titleField')}</Label>
          <Input {...form.register('title')} />
          {form.formState.errors.title ? (
            <p className="text-sm text-destructive">{t('workspace.adoption.required')}</p>
          ) : null}
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ChangeAdoptionKindValues.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {t(`workspace.adoption.kind.${kind}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <Label>{t('workspace.adoption.owner')}</Label>
          <Controller
            control={form.control}
            name="ownerUserId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('workspace.adoption.none')}</SelectItem>
                  {participants.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <Label>{t('workspace.adoption.target')}</Label>
          <Input {...form.register('target')} />
          <Label>{t('workspace.adoption.dueDate')}</Label>
          <Input type="date" {...form.register('dueDate')} />
          <Textarea {...form.register('detail')} placeholder={t('workspace.adoption.detail')} />
          <Button type="submit" size="sm" disabled={create.isPending}>
            {t('workspace.adoption.add')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
